import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { ArrowLeft, Save, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { SymptomHeatmap } from '@/components/shared/SymptomHeatmap';
import { API } from '@/lib/api';



export default function HashimotoModule() {
  const { language } = useLanguage();
  const { getHeaders } = useAuth();
  const nav = useNavigate();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [trends, setTrends] = useState(null);
  const [log, setLog] = useState({
    date: today, tsh: '', t3: '', t4: '', selenium_score: 5, omega3_score: 5,
    symptoms: [], medications: [], notes: ''
  });

  const de = language === 'de';
  const symptomOptions = de
    ? ['Müdigkeit', 'Gewichtszunahme', 'Kälteempfindlichkeit', 'Haarausfall', 'Trockene Haut', 'Verstopfung', 'Gehirnnebel', 'Gelenkschmerzen']
    : ['Fatigue', 'Weight gain', 'Cold sensitivity', 'Hair loss', 'Dry skin', 'Constipation', 'Brain fog', 'Joint pain'];

  const fetchData = useCallback(async () => {
    try {
      const h = getHeaders();
      const [logRes, histRes, trendRes] = await Promise.all([
        axios.get(`${API}/modules/hashimoto/log/${today}`, { headers: h }),
        axios.get(`${API}/modules/hashimoto/history?days=90`, { headers: h }),
        axios.get(`${API}/modules/hashimoto/trends`, { headers: h }).catch(() => ({ data: null })),
      ]);
      if (logRes.data?.tsh !== undefined && logRes.data.tsh !== null) {
        setLog(prev => ({ ...prev, ...logRes.data, tsh: logRes.data.tsh || '', t3: logRes.data.t3 || '', t4: logRes.data.t4 || '' }));
      }
      setHistory(histRes.data.reverse());
      if (trendRes.data) setTrends(trendRes.data);
    } catch (e) { console.error(e); }
  }, [today]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/modules/hashimoto/log`, {
        ...log, tsh: log.tsh ? parseFloat(log.tsh) : null, t3: log.t3 ? parseFloat(log.t3) : null, t4: log.t4 ? parseFloat(log.t4) : null,
      }, { headers: getHeaders() });
      toast.success(de ? 'Gespeichert!' : 'Saved!');
      fetchData();
    } catch { toast.error(de ? 'Fehler' : 'Error'); }
    setSaving(false);
  };

  const toggleSymptom = (s) => setLog(prev => ({
    ...prev, symptoms: prev.symptoms.includes(s) ? prev.symptoms.filter(x => x !== s) : [...prev.symptoms, s]
  }));

  const chartData = history.filter(h => h.tsh || h.t3 || h.t4).map(h => ({
    date: h.date?.slice(5), tsh: h.tsh, ft3: h.t3, ft4: h.t4
  }));

  return (
    <div className="space-y-6 pb-24 lg:pb-8 max-w-3xl" data-testid="hashimoto-module">
      <div className="flex items-center gap-3">
        <button onClick={() => nav('/dashboard')} className="text-foreground/40 hover:text-foreground transition-colors" data-testid="hashimoto-back"><ArrowLeft size={20} /></button>
        <div>
          <Badge className="bg-purple-100 text-purple-700 border-0 rounded-full font-body text-xs mb-1">{de ? 'Modul 3' : 'Module 3'}</Badge>
          <h1 className="font-display text-3xl font-medium tracking-tight">Hashimoto</h1>
        </div>
      </div>

      {/* Warnings */}
      {trends?.warnings?.length > 0 && (
        <div className="space-y-2" data-testid="hashimoto-warnings">
          {trends.warnings.map((w, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-red-50 border border-red-200">
              <AlertTriangle size={18} className="text-red-500 shrink-0" />
              <p className="font-body text-sm text-red-700">{de ? w.message_de : w.message_en}</p>
            </div>
          ))}
        </div>
      )}

      {/* Lab Values */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6 shadow-sm" data-testid="hashimoto-labs-card">
        <h2 className="font-display text-xl font-medium mb-4">{de ? 'Laborwerte' : 'Lab Values'}</h2>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="font-body text-xs text-foreground/60">TSH (mU/L)</Label>
            <Input type="number" step="0.01" value={log.tsh} onChange={e => setLog({ ...log, tsh: e.target.value })} className="mt-1 rounded-xl" data-testid="hashimoto-tsh" placeholder="0.5-4.5" />
            {log.tsh && parseFloat(log.tsh) > 4.0 && <p className="text-[10px] text-red-500 mt-1">{de ? 'Erhöt - Ärztin konsultieren' : 'Elevated - consult doctor'}</p>}
            {log.tsh && parseFloat(log.tsh) >= 0.5 && parseFloat(log.tsh) <= 2.5 && <p className="text-[10px] text-green-500 mt-1">{de ? 'Optimal' : 'Optimal'}</p>}
          </div>
          <div>
            <Label className="font-body text-xs text-foreground/60">fT3 (pmol/L)</Label>
            <Input type="number" step="0.1" value={log.t3} onChange={e => setLog({ ...log, t3: e.target.value })} className="mt-1 rounded-xl" data-testid="hashimoto-t3" placeholder="3.1-6.8" />
          </div>
          <div>
            <Label className="font-body text-xs text-foreground/60">fT4 (pmol/L)</Label>
            <Input type="number" step="0.1" value={log.t4} onChange={e => setLog({ ...log, t4: e.target.value })} className="mt-1 rounded-xl" data-testid="hashimoto-t4" placeholder="12-22" />
          </div>
        </div>
      </Card>

      {/* Multi-Line Lab Trend Chart */}
      {chartData.length > 1 && (
        <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="hashimoto-trend-chart">
          <h2 className="font-display text-xl font-medium mb-4">{de ? 'Laborwerte-Verlauf' : 'Lab Value Trends'}</h2>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D6" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: 'Mulish' }} />
              <YAxis tick={{ fontSize: 11, fontFamily: 'Mulish' }} />
              <Tooltip contentStyle={{ borderRadius: 12, fontFamily: 'Mulish', fontSize: 12 }} />
              <Legend wrapperStyle={{ fontFamily: 'Mulish', fontSize: 12 }} />
              <Line type="monotone" dataKey="tsh" name="TSH" stroke="#8B5CF6" strokeWidth={2.5} dot={{ fill: '#8B5CF6', r: 3 }} />
              <Line type="monotone" dataKey="ft3" name="fT3" stroke="#EC4899" strokeWidth={2} dot={{ fill: '#EC4899', r: 3 }} />
              <Line type="monotone" dataKey="ft4" name="fT4" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B', r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Supplements */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="hashimoto-supplements-card">
        <h2 className="font-display text-xl font-medium mb-4">{de ? 'Nährstoffe' : 'Nutrients'}</h2>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between">
              <Label className="font-body text-sm text-foreground/70">{de ? 'Selen-Score' : 'Selenium Score'} ({log.selenium_score}/10)</Label>
              {log.selenium_score < 4 && <span className="text-[10px] text-amber-600 font-body">{de ? '2-3 Paranüsse/Tag empfohlen' : '2-3 Brazil nuts/day recommended'}</span>}
            </div>
            <Slider value={[log.selenium_score]} onValueChange={([v]) => setLog({ ...log, selenium_score: v })} min={1} max={10} step={1} className="mt-2 [&_[role=slider]]:bg-purple-500 [&_[role=slider]]:border-purple-500" data-testid="hashimoto-selenium" />
          </div>
          <div>
            <Label className="font-body text-sm text-foreground/70">Omega-3 Score ({log.omega3_score}/10)</Label>
            <Slider value={[log.omega3_score]} onValueChange={([v]) => setLog({ ...log, omega3_score: v })} min={1} max={10} step={1} className="mt-2 [&_[role=slider]]:bg-purple-500 [&_[role=slider]]:border-purple-500" data-testid="hashimoto-omega3" />
          </div>
        </div>
      </Card>

      {/* Symptoms + Heatmap */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="hashimoto-symptoms-card">
        <h2 className="font-display text-xl font-medium mb-4">{de ? 'Symptome heute' : 'Symptoms Today'}</h2>
        <div className="flex flex-wrap gap-2 mb-6">
          {symptomOptions.map(s => (
            <button key={s} onClick={() => toggleSymptom(s)} className={`px-3 py-1.5 rounded-full font-body text-sm transition-all border ${log.symptoms.includes(s) ? 'bg-purple-500 text-white border-purple-500' : 'bg-white text-foreground/60 border-border/60 hover:border-purple-300'}`} data-testid={`symptom-${s}`}>{s}</button>
          ))}
        </div>
        {history.length > 7 && (
          <SymptomHeatmap
            data={history}
            metric={e => (e.symptoms?.length || 0) * 2}
            title={de ? 'Symptom-Intensität (letzte 4 Wochen)' : 'Symptom Intensity (last 4 weeks)'}
          />
        )}
      </Card>

      {/* Top Symptoms Frequency */}
      {trends?.symptom_frequency && Object.keys(trends.symptom_frequency).length > 0 && (
        <Card className="bg-white border border-border/60 rounded-2xl p-6" data-testid="hashimoto-symptom-freq">
          <h2 className="font-display text-lg font-medium mb-3">{de ? 'Häufigste Symptome' : 'Most Frequent Symptoms'}</h2>
          <div className="space-y-2">
            {Object.entries(trends.symptom_frequency).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([sym, count]) => (
              <div key={sym} className="flex items-center gap-3">
                <span className="font-body text-sm text-foreground/70 w-32 truncate">{sym}</span>
                <div className="flex-1 bg-warmgray/50 rounded-full h-2">
                  <div className="bg-purple-400 h-2 rounded-full" style={{ width: `${Math.min(100, (count / trends.total_entries) * 100)}%` }} />
                </div>
                <span className="font-body text-xs text-foreground/40 w-8 text-right">{count}x</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Textarea value={log.notes} onChange={e => setLog({ ...log, notes: e.target.value })} className="rounded-xl" rows={2} placeholder={de ? 'Notizen...' : 'Notes...'} data-testid="hashimoto-notes" />

      <Button onClick={handleSave} disabled={saving} className="w-full md:w-auto rounded-full bg-purple-500 hover:bg-purple-600 text-white px-10 py-5 text-base font-medium shadow-lg shadow-purple-500/20" data-testid="hashimoto-save-btn">
        <Save size={18} className="mr-2" /> {saving ? '...' : de ? 'Speichern' : 'Save'}
      </Button>
    </div>
  );
}
